import { useEffect, useState } from 'react';

import useCustomsDetails from '@/features/Shipping/components/CustomsDetails/hooks/useCustomsDetails';

import useAddFirstZone from '../components/AddFirstZone/hooks/useAddFirstZone';
import useProfileDetails from '../components/ProfileDetails/hooks/useProfileDetails';
import useSetupOptions from '../components/SetupOptions/hooks/useSetupOptions';
import useFetchPaymentMethods from './useFetchPaymentMethods';

export const viewId = Object.freeze({
  setup_options: 'setup options',
  profile_details: 'profile details',
  add_zone: 'add zone',
  customs_details: 'customs details',
  product_variants: 'assign products and variants',
  error: 'error',
});

type Args = {
  errors?: string[];
  initialView?: keyof typeof viewId;
};

const useCreateEditShippingProfileDialog = ({ errors, initialView }: Args) => {
  const [currentView, setCurrentView] =
    useState<keyof typeof viewId>('setup_options');

  const setupOptionsState = useSetupOptions();
  const profileDetailsState = useProfileDetails();
  const addFirstZoneState = useAddFirstZone();
  const customsDetailsState = useCustomsDetails();

  const { providerPaymentMethods } = useFetchPaymentMethods({
    providerId: setupOptionsState.setupOptionsForm.shipping_provider_id,
  });

  useEffect(() => {
    if (errors && errors.length > 0) {
      setCurrentView('error');
    }
  }, [errors]);

  useEffect(() => {
    if (initialView) {
      setCurrentView(initialView);
    }
  }, [initialView]);

  const validateByView = (view: keyof typeof viewId) => {
    switch (view) {
      case 'setup_options':
        return setupOptionsState.onValidate();
      case 'profile_details':
        return profileDetailsState.onValidate();
      case 'add_zone':
        return addFirstZoneState.onValidate();
      case 'customs_details':
        return customsDetailsState.onValidate();
      default:
        return true;
    }
  };

  const getView = (
    view: keyof typeof viewId,
  ): {
    prevView: keyof typeof viewId | null;
    nextView: keyof typeof viewId | null;
  } => {
    const { ignore_customs_details } = setupOptionsState.setupOptionalStepsForm;

    switch (view) {
      case 'setup_options':
        return {
          prevView: null,
          nextView: 'profile_details',
        };
      case 'profile_details':
        return {
          prevView: 'setup_options',
          nextView: 'add_zone',
        };
      case 'add_zone':
        return {
          prevView: 'profile_details',
          nextView: ignore_customs_details
            ? 'product_variants'
            : 'customs_details',
        };
      case 'customs_details':
        return {
          prevView: 'add_zone',
          nextView: 'product_variants',
        };
      case 'product_variants':
        return {
          prevView: ignore_customs_details ? 'add_zone' : 'customs_details',
          nextView: null,
        };
      case 'error':
        return {
          prevView: 'setup_options',
          nextView: null,
        };
      default: {
        throw new Error(`Unhandled view: ${view as string}`);
      }
    }
  };

  const showNextView = () => {
    const isValid = validateByView(currentView);
    const { nextView } = getView(currentView);

    if (isValid && nextView) {
      setCurrentView(nextView);
    }
  };

  const showPrevView = () => {
    const { prevView } = getView(currentView);

    if (prevView) {
      setCurrentView(prevView);
    }
  };

  return {
    setupOptionsState,
    profileDetailsState,
    addFirstZoneState,
    customsDetailsState,
    providerPaymentMethods,
    currentView,
    showNextView,
    showPrevView,
    getView,
    setCurrentView,
  };
};

export default useCreateEditShippingProfileDialog;
